import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import BrandLogo from './BrandLogo';

/**
 * Reusable layout for static "legal" pages (Privacy / Terms / Refund / Contact / About).
 * Keeps the visual language consistent and lets every page share footer + back link.
 */
export default function LegalLayout({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-200 flex flex-col">
      {/* Top bar */}
      <header className="border-b border-slate-800/60 bg-slate-950/60 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center hover:opacity-80 transition" aria-label="Facade Lighting Studio home">
            <BrandLogo className="w-28" />
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-amber-400 transition">
            Open Studio →
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-1">{title}</h1>
        {lastUpdated && (
          <p className="text-xs text-slate-500 mb-8">Last updated: {lastUpdated}</p>
        )}
        <div className="prose prose-invert prose-sm max-w-none space-y-5 text-slate-300 [&_h2]:text-white [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-2 [&_h3]:text-white [&_h3]:font-semibold [&_h3]:mt-4 [&_a]:text-amber-400 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_strong]:text-white">
          {children}
        </div>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-800/60 bg-slate-950/60 mt-12">
      <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-slate-500">
        <div className="flex items-center gap-3">
          <BrandLogo className="w-20" />
          <span>© {new Date().getFullYear()} All rights reserved.</span>
        </div>
        <nav className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/pricing" className="hover:text-amber-400 transition">Pricing</Link>
          <Link to="/privacy" className="hover:text-amber-400 transition">Privacy</Link>
          <Link to="/terms" className="hover:text-amber-400 transition">Terms</Link>
          <Link to="/refund" className="hover:text-amber-400 transition">Refund</Link>
          <Link to="/contact" className="hover:text-amber-400 transition flex items-center gap-1">
            <Mail className="w-3 h-3" /> Contact
          </Link>
        </nav>
      </div>
    </footer>
  );
}
